import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// MUI per-component imports, matching the convention in QuizTracking.js.
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import { getVLearnLearners } from "../../../containers/vlearn/vlearnSlice";
import {
    VLearnEmptyState, VLearnProgressBar, VLearnStatusChip,
    isVLearnOverdue, vlearnSurface, vlearnType
} from "../vlearnUi";
import CollabDetailDialog from "./CollabDetailDialog";
import MobileLearnerProgressList from "../../../containers/vlearn/mobile/MobileLearnerProgressList";

// The row rhythm used by every other DataGrid in the application (see VResumeList), so
// moving between screens does not change the density under the reader. Two lines of
// name + team need more than the 46px those grids use.
const ROW_HEIGHT = 54
const HEADER_HEIGHT = 44

// How long the search box waits after the last keystroke before it queries. 350ms is long
// enough that typing a surname is one request rather than eight, and short enough that the
// table does not feel detached from the keyboard.
const SEARCH_DEBOUNCE_MS = 350

const PAGE_SIZES = [25, 50, 100]

// `_buildLearnerRows` filters on exactly these three, and validates against the same list.
// Offering a fourth here would produce a filter the server silently ignores.
const STATUS_FILTERS = ['not_started', 'in_progress', 'completed']

/**
 * The learner table — "who did well, and who did not".
 *
 * One row per active enrolment, straight from `VLearnProgressController.getLearnerProgress`.
 * Every field rendered below is one that endpoint sends; nothing is derived here that the
 * server already decided, because two places computing "is this learner late" is two answers.
 *
 * ── Why the search, the filters and the paging all go to the server ───────────────────
 *
 * `getLearnerProgress` paginates and filters in SQL and returns a `total` alongside the page.
 * A course assigned to every team in the company has thousands of enrolments; fetching them
 * all so the browser can filter a string would be a multi-megabyte response on every visit,
 * and the `total` shown under the table would stop meaning anything. So each change of
 * search, status, team or page is a new request, and the grid runs in
 * `paginationMode="server"`.
 *
 * Column sorting is off for the same reason. The server orders by surname; letting the grid
 * re-sort the fifty rows it happens to be holding would present "the lowest progress" when it
 * is only the lowest progress on page three.
 *
 * @param {object} props
 * @param {number} props.idCourse
 * @param {(idCollab: number) => void} [props.onOpenCollab] Notified when a row is opened, for
 *   a parent that wants to react; the dialog opens either way.
 */
export default function LearnerProgressTable({ idCourse, onOpenCollab }) {

    const dispatch = useDispatch()

    const rows = useSelector((state) => state.vlearn.dashboardLearners)
    const total = useSelector((state) => state.vlearn.dashboardLearnersTotal)
    const lang = useSelector((state) => state.settings.lang)

    /*
     * `isMobile === 2` is the platform's phone flag; the media query covers a desktop browser
     * narrowed to a phone width, which is how this is checked in development. Either one
     * swaps the 992px DataGrid for a card list — see MobileLearnerProgressList for why a
     * horizontally dragged table is not a table on a phone.
     */
    const isMobileRedux = useSelector((state) => state.settings.isMobile)
    const theme = useTheme()
    const isNarrowQuery = useMediaQuery(theme.breakpoints.down('sm'))
    const isPhone = isMobileRedux === 2 || isNarrowQuery

    const [searchInput, setSearchInput] = useState("")
    // The value actually sent. Separate from `searchInput` so the field stays responsive
    // while the request is debounced behind it.
    const [search, setSearch] = useState("")
    const [status, setStatus] = useState("")
    const [idTeam, setIdTeam] = useState("")
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZES[0] })
    const [openCollab, setOpenCollab] = useState(null)

    const debounceTimer = useRef(null)
    // The last request actually issued, serialised. React 18's StrictMode mounts every
    // component twice in development, and without this the first paint fires two identical
    // queries; it also absorbs a re-render that changes nothing this endpoint cares about.
    const lastQuery = useRef(null)

    // ── Debounce the search box ──────────────────────────────────────────────────────
    //
    // A `useRef` timer rather than a debounce utility: the project's rule is no new
    // dependency, and one timer cleared on unmount is the whole of what is needed here.
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => {
            setSearch(searchInput.trim())
            // A new search invalidates whatever page the reader was on: page 4 of the
            // unfiltered list is not page 4 of the filtered one.
            setPaginationModel((current) => ({ ...current, page: 0 }))
        }, SEARCH_DEBOUNCE_MS)
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current)
        }
    }, [searchInput])

    // ── Fetch whenever the server-side query changes ─────────────────────────────────
    useEffect(() => {
        if (!idCourse) return
        const body = {
            id_course: idCourse,
            search: search || undefined,
            status: status || undefined,
            id_team: idTeam || undefined,
            limit: paginationModel.pageSize,
            offset: paginationModel.page * paginationModel.pageSize
        }
        const key = JSON.stringify(body)
        if (lastQuery.current === key) return
        lastQuery.current = key
        dispatch(getVLearnLearners(body))
    }, [dispatch, idCourse, search, status, idTeam, paginationModel.page, paginationModel.pageSize])

    /**
     * The team filter's options.
     *
     * The endpoint returns a page of learners, not a facet count, and there is no thunk that
     * answers "which teams are enrolled on this course" — so the options are the teams seen
     * in the pages loaded so far, accumulated and never removed. The first load is unfiltered
     * and ordered by surname, so the teams a reader is likely to want appear immediately, and
     * paging through reveals the rest. Offering the company's entire team list instead would
     * fill the menu with teams that have nobody on this course.
     */
    const [teamOptions, setTeamOptions] = useState([])
    useEffect(() => {
        setTeamOptions((current) => {
            const byId = new Map(current.map((team) => [String(team.id), team]))
            let added = false
            for (const row of (rows || [])) {
                if (!row.id_team || !row.team_name) continue
                const id = String(row.id_team)
                if (byId.has(id)) continue
                byId.set(id, { id: row.id_team, name: row.team_name })
                added = true
            }
            if (!added) return current
            return Array.from(byId.values())
                .sort((a, b) => String(a.name).localeCompare(String(b.name)))
        })
    }, [rows])

    const hasFilters = Boolean(search || status || idTeam)

    const clearFilters = useCallback(() => {
        setSearchInput("")
        setSearch("")
        setStatus("")
        setIdTeam("")
        setPaginationModel((current) => ({ ...current, page: 0 }))
    }, [])

    const onRowOpen = useCallback((idCollab) => {
        setOpenCollab(idCollab)
        if (onOpenCollab) onOpenCollab(idCollab)
    }, [onOpenCollab])

    /** A date cell, or an em dash. Never the string "Invalid date". */
    const formatDate = useCallback((value, withTime) => {
        if (!value) return '—'
        const parsed = moment(value)
        if (!parsed.isValid()) return '—'
        // `.locale(lang)` on the instance rather than `moment.locale(lang)`: the global is
        // already set once from the stored language in app/lang/langs.js, and a component
        // that reaches in and changes it affects every other screen holding a moment.
        return parsed.locale(lang || 'fr').format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
    }, [lang])

    const columns = useMemo(() => [
        {
            field: 'collab',
            headerName: Localize("VLEARN_COL_COLLAB", lang),
            flex: 1.7,
            minWidth: 230,
            sortable: false,
            // The rows carry `nom`/`prenom`, not `collab`, so the grid has nothing to read
            // without this. Signature is the x-data-grid v8 `(value, row)` shape — the v6
            // `(params)` form silently yields undefined.
            valueGetter: (value, row) => `${row.prenom || ''} ${row.nom || ''}`.trim(),
            renderCell: (params) => {
                const row = params.row
                const photoUrl = NetworkManager.getInstance()
                    .getCollabPhotoUrl(row.id_collab, row.photo_updated_at)
                const initials = [row.prenom, row.nom].filter(Boolean)
                    .map((part) => String(part).charAt(0).toUpperCase()).join('')
                // A NULL `team_name` on an enrolment means the person was targeted by name
                // rather than through a team — a different fact from "has no team", which is
                // why it gets its own label instead of an em dash.
                const teamLabel = row.team_name || Localize("VLEARN_TEAM_INDIVIDUAL", lang)
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Avatar
                            src={photoUrl || undefined}
                            sx={{
                                width: 32, height: 32, fontSize: 12,
                                bgcolor: 'var(--lightGrey, #ededed)', color: '#6f6f6f'
                            }}>
                            {initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ ...vlearnType.cardTitle }}>
                                {[row.prenom, row.nom].filter(Boolean).join(' ')}
                            </Typography>
                            <Typography noWrap sx={{ ...vlearnType.meta }}>
                                {teamLabel}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            field: 'status',
            headerName: Localize("VLEARN_COL_STATUS", lang),
            width: 132,
            sortable: false,
            renderCell: (params) => (
                // `is_overdue` is the server's own comparison against CURDATE(); the local
                // helper is the fallback for a row that predates it rather than a second
                // opinion about it.
                <VLearnStatusChip
                    status={params.row.status}
                    overdue={params.row.is_overdue
                        || isVLearnOverdue(params.row.due_date, params.row.status)}
                />
            )
        },
        {
            field: 'progress_percent',
            headerName: Localize("VLEARN_COL_PROGRESS", lang),
            flex: 1,
            minWidth: 170,
            sortable: false,
            renderCell: (params) => {
                const percent = Number(params.row.progress_percent) || 0
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
                        <Box sx={{ flex: 1, minWidth: 60 }}>
                            <VLearnProgressBar percent={percent} status={params.row.status} />
                        </Box>
                        <Typography sx={{
                            fontSize: '0.8rem', fontWeight: 700, minWidth: 40, textAlign: 'right',
                            fontVariantNumeric: 'tabular-nums',
                            color: 'var(--text-primary, #111318)'
                        }}>
                            {percent}%
                        </Typography>
                    </Box>
                )
            }
        },
        {
            field: 'lessons',
            headerName: Localize("VLEARN_COL_LESSONS_DONE", lang),
            width: 100,
            sortable: false,
            align: 'right',
            headerAlign: 'right',
            valueGetter: (value, row) =>
                `${Number(row.lessons_completed) || 0} / ${Number(row.lessons_total) || 0}`,
            renderCell: (params) => (
                <Typography sx={{
                    fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums',
                    color: 'var(--text-primary, #111318)'
                }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'best_quiz_percent',
            headerName: Localize("VLEARN_COL_BEST_QUIZ", lang),
            width: 132,
            sortable: false,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => {
                // NULL means "has not been graded", which is not the same statement as 0%.
                const value = params.row.best_quiz_percent
                if (value === null || typeof value === 'undefined') {
                    return <Typography sx={{ ...vlearnType.meta }}>—</Typography>
                }
                return (
                    <Typography sx={{
                        fontSize: '0.82rem', fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: 'var(--text-primary, #111318)'
                    }}>
                        {Math.round(Number(value) * 10) / 10}%
                    </Typography>
                )
            }
        },
        {
            field: 'quiz_passed',
            headerName: Localize("VLEARN_COL_QUIZ_PASSED", lang),
            width: 92,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.row.quiz_passed
                if (value === null || typeof value === 'undefined') {
                    return <Typography sx={{ ...vlearnType.meta }}>—</Typography>
                }
                // The icon carries a label rather than standing on colour alone, so the cell
                // still reads for someone who cannot separate the green from the grey.
                const passed = Boolean(Number(value))
                const label = Localize(passed ? "VLEARN_YES" : "VLEARN_NO", lang)
                return (
                    <Tooltip title={label} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center' }} aria-label={label}>
                            {passed
                                ? <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'var(--green, rgb(58,156,96))' }} />
                                : <RemoveCircleOutlineRoundedIcon sx={{ fontSize: 18, color: '#9aa0ad' }} />}
                        </Box>
                    </Tooltip>
                )
            }
        },
        {
            field: 'last_accessed_at',
            headerName: Localize("VLEARN_COL_LAST_ACCESS", lang),
            width: 148,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{
                    fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums',
                    color: 'var(--text-secondary, #4b5263)'
                }}>
                    {formatDate(params.row.last_accessed_at, true)}
                </Typography>
            )
        },
        {
            field: 'due_date',
            headerName: Localize("VLEARN_COL_DUE_DATE", lang),
            width: 120,
            sortable: false,
            renderCell: (params) => {
                const overdue = params.row.is_overdue
                    || isVLearnOverdue(params.row.due_date, params.row.status)
                return (
                    <Typography sx={{
                        fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums',
                        fontWeight: overdue ? 700 : 400,
                        color: overdue ? '#B3271F' : 'var(--text-secondary, #4b5263)'
                    }}>
                        {formatDate(params.row.due_date, false)}
                    </Typography>
                )
            }
        },
        {
            field: 'actions',
            headerName: '',
            width: 52,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            // The row itself opens on click, but a click is not the only way people use a
            // table: the grid's cells take focus and Enter does not fire `onRowClick`, so
            // without this button the detail dialog is unreachable from the keyboard and
            // unnamed to a screen reader.
            renderCell: (params) => (
                <Tooltip title={Localize("VLEARN_OPEN_COLLAB_DETAIL", lang)} arrow>
                    <IconButton
                        size="small"
                        aria-label={Localize("VLEARN_OPEN_COLLAB_DETAIL", lang)}
                        onClick={(event) => {
                            // Otherwise the row's own handler runs too and opens the same
                            // dialog a second time.
                            event.stopPropagation()
                            onRowOpen(params.row.id_collab)
                        }}
                        sx={{
                            '&:focus-visible': {
                                outline: '2px solid var(--orange, rgb(255,144,21))',
                                outlineOffset: 1
                            }
                        }}
                    >
                        <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            )
        }
    ], [lang, formatDate, onRowOpen])

    // The course genuinely has nobody on it, as opposed to nobody matching the filters —
    // two different problems with two different next actions, so two different panels.
    const isCourseEmpty = !hasFilters && total === 0 && (rows || []).length === 0

    if (isCourseEmpty) {
        return (
            <Box sx={{ ...vlearnSurface.card }}>
                <VLearnEmptyState
                    icon={<GroupsOutlinedIcon />}
                    title={<Langs str="VLEARN_NO_LEARNERS_TITLE" />}
                    description={<Langs str="VLEARN_NO_LEARNERS_BODY" />}
                />
            </Box>
        )
    }

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                {/* ── Filters ─────────────────────────────────────────────────────── */}
                {/* Stacked below `sm`: a search box and two selects sharing a ~300px row
                    wrapped into three lines of controls too narrow to read their own
                    values. One full-width control per line is the same total height and
                    legible. */}
                <Box sx={{
                    display: 'flex', gap: 1.25,
                    flexDirection: { xs: 'column', sm: 'row' },
                    // `wrap` is row-axis only; in a column it spills into a second column.
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                    <TextField
                        size="small"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder={Localize("VLEARN_SEARCH_LEARNERS", lang)}
                        inputProps={{ 'aria-label': Localize("VLEARN_SEARCH_LEARNERS", lang) }}
                        sx={{
                            // The basis is a HEIGHT below `sm`, where this row is a column —
                            // `0 1 300px` made the field a 300px-tall box. Content-sized when
                            // stacked; the width intent applies only once it is a row again.
                            flex: { xs: '0 0 auto', sm: '0 1 300px' },
                            width: { xs: '100%', sm: 'auto' }
                        }}
                        InputProps={{
                            // 16px below `sm` or iOS Safari zooms the page on focus and does
                            // not zoom back — see `vlearnInputSx`.
                            sx: { fontSize: { xs: '16px', sm: 13 } },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRoundedIcon sx={{ fontSize: 18, color: '#a0a5b1' }} />
                                </InputAdornment>
                            )
                        }}
                    />

                    <Select
                        size="small"
                        value={status}
                        displayEmpty
                        inputProps={{ 'aria-label': Localize("VLEARN_COL_STATUS", lang) }}
                        onChange={(event) => {
                            setStatus(event.target.value)
                            setPaginationModel((current) => ({ ...current, page: 0 }))
                        }}
                        sx={{
                            flex: { xs: '0 0 auto', sm: '0 1 180px' }, width: { xs: '100%', sm: 'auto' },
                            fontSize: { xs: '16px', sm: 13 }
                        }}>
                        <MenuItem value="" sx={{ fontSize: 13 }}>
                            <Langs str="VLEARN_FILTER_STATUS_ALL" />
                        </MenuItem>
                        {STATUS_FILTERS.map((value) => (
                            <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>
                                {Localize(`VLEARN_STATUS_${value.toUpperCase()}`, lang)}
                            </MenuItem>
                        ))}
                    </Select>

                    <Select
                        size="small"
                        value={idTeam}
                        displayEmpty
                        inputProps={{ 'aria-label': Localize("VLEARN_FILTER_TEAM_ALL", lang) }}
                        onChange={(event) => {
                            setIdTeam(event.target.value)
                            setPaginationModel((current) => ({ ...current, page: 0 }))
                        }}
                        sx={{
                            flex: { xs: '0 0 auto', sm: '0 1 200px' }, width: { xs: '100%', sm: 'auto' },
                            fontSize: { xs: '16px', sm: 13 }
                        }}>
                        <MenuItem value="" sx={{ fontSize: 13 }}>
                            <Langs str="VLEARN_FILTER_TEAM_ALL" />
                        </MenuItem>
                        {teamOptions.map((team) => (
                            <MenuItem key={team.id} value={team.id} sx={{ fontSize: 13 }}>
                                {team.name}
                            </MenuItem>
                        ))}
                    </Select>

                    <Box sx={{ flex: 1 }} />

                    <Typography sx={{ ...vlearnType.meta, fontVariantNumeric: 'tabular-nums' }}>
                        <Langs
                            str={total === 1 ? "VLEARN_LEARNERS_COUNT_ONE" : "VLEARN_LEARNERS_COUNT_MANY"}
                            params={{ count: total }}
                        />
                    </Typography>
                </Box>

                {/* ── The grid ────────────────────────────────────────────────────── */}
                {/*
                  * `overflowX: 'auto'` on the wrapper, not on the page: eight columns do not
                  * fit a narrow viewport, and a table that widens the document body makes the
                  * whole application scroll sideways.
                  */}
                {isPhone ? (
                    <MobileLearnerProgressList
                        rows={rows}
                        total={total}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        onRowOpen={onRowOpen}
                        formatDate={formatDate}
                        lang={lang}
                    />
                ) : (
                <Box sx={{ ...vlearnSurface.card, width: '100%', overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 992 }}>
                        <DataGrid
                            rows={rows || []}
                            columns={columns}
                            getRowId={(row) => row.id_collab}
                            rowHeight={ROW_HEIGHT}
                            columnHeaderHeight={HEADER_HEIGHT}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            paginationMode="server"
                            rowCount={total || 0}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={PAGE_SIZES}
                            onRowClick={(params) => onRowOpen(params.row.id_collab)}
                            localeText={{ noRowsLabel: Localize("VLEARN_NO_ROWS", lang) }}
                            autoHeight
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-columnHeaderTitle': {
                                    fontSize: 12, fontWeight: 700, color: '#5a6070'
                                },
                                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#fafbfd' },
                                /*
                                 * x-data-grid v8 does not make the cell a flex container: it
                                 * centres bare text with a line-height near the row height,
                                 * which leaves a `renderCell` wrapper pinned to the top of
                                 * the cell and inherits that line-height into its own text.
                                 * Every column here renders through `renderCell`, so making
                                 * the cell flex costs nothing and fixes both. Same note as
                                 * VResumeList.
                                 */
                                '& .MuiDataGrid-cell': {
                                    borderColor: '#f1f2f6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 'normal'
                                },
                                '& .MuiDataGrid-row': { cursor: 'pointer' },
                                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(255,144,21,0.05)' },
                                // The grid's rows are focusable; without a visible ring a
                                // keyboard user cannot tell which one Enter would open.
                                '& .MuiDataGrid-cell:focus-visible': {
                                    outline: '2px solid var(--orange, rgb(255,144,21))',
                                    outlineOffset: '-2px'
                                },
                                '& .MuiDataGrid-footerContainer': { borderColor: '#f1f2f6' }
                            }}
                        />
                    </Box>
                </Box>
                )}

                {/* Filters that match nothing: say so, and offer the way out. */}
                {hasFilters && total === 0 ? (
                    <Box sx={{ ...vlearnSurface.card }}>
                        <VLearnEmptyState
                            icon={<SearchRoundedIcon />}
                            title={<Langs str="VLEARN_NO_LEARNERS_MATCH_TITLE" />}
                            description={<Langs str="VLEARN_NO_LEARNERS_MATCH_BODY" />}
                            action={
                                <Button size="small" variant="outlined" onClick={clearFilters}>
                                    <Langs str="VLEARN_CLEAR_FILTERS" />
                                </Button>
                            }
                        />
                    </Box>
                ) : null}
            </Box>

            <CollabDetailDialog
                open={Boolean(openCollab)}
                idCourse={idCourse}
                idCollab={openCollab}
                onClose={() => setOpenCollab(null)}
            />
        </>
    )
}
